import { useState, useEffect, useCallback } from 'react';
import { tags as tagsApi, trades as tradesApi } from '../api/client';

const CATEGORIES = [
  { value: 'setup', label: 'Setup' },
  { value: 'session_time', label: 'Session Time' },
  { value: 'emotion', label: 'Emotion' },
  { value: 'other', label: 'Other' },
];

const PRESET_COLORS = [
  '#6366f1', '#22c55e', '#ef4444', '#f59e0b',
  '#3b82f6', '#ec4899', '#14b8a6', '#8b5cf6',
];

export default function TagManager({ sessionId, tradeId, currentTags, onTagsChange }) {
  const [allTags, setAllTags] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState('setup');
  const [newColor, setNewColor] = useState(PRESET_COLORS[0]);

  useEffect(() => {
    tagsApi.list().then((res) => setAllTags(res.data)).catch(() => {});
  }, []);

  const currentIds = currentTags.map((t) => t.id);

  const toggleTag = useCallback(
    (tagId) => {
      const newIds = currentIds.includes(tagId)
        ? currentIds.filter((id) => id !== tagId)
        : [...currentIds, tagId];

      tradesApi.update(sessionId, tradeId, { tag_ids: newIds }).then((res) => {
        onTagsChange(res.data.tags);
      }).catch((err) => console.error('Failed to update tags:', err));
    },
    [sessionId, tradeId, currentIds, onTagsChange]
  );

  const removeTag = useCallback(
    (tagId) => {
      const newIds = currentIds.filter((id) => id !== tagId);
      tradesApi.update(sessionId, tradeId, { tag_ids: newIds }).then((res) => {
        onTagsChange(res.data.tags);
      }).catch((err) => console.error('Failed to remove tag:', err));
    },
    [sessionId, tradeId, currentIds, onTagsChange]
  );

  const handleCreateTag = (e) => {
    e.preventDefault();
    if (!newName.trim()) return;

    tagsApi.create({ name: newName.trim(), category: newCategory, color: newColor })
      .then((res) => {
        setAllTags((prev) => [...prev, res.data]);
        toggleTag(res.data.id);
        setNewName('');
        setCreating(false);
      })
      .catch((err) => console.error('Failed to create tag:', err));
  };

  const grouped = CATEGORIES.map((cat) => ({
    ...cat,
    tags: allTags.filter((t) => t.category === cat.value),
  })).filter((g) => g.tags.length > 0);

  return (
    <div className="tag-manager">
      <div className="tag-pills-row">
        {currentTags.map((tag) => (
          <span key={tag.id} className="tag-pill" style={{ borderColor: tag.color }}>
            <span className="tag-dot" style={{ background: tag.color }} />
            {tag.name}
            <button className="tag-remove" onClick={() => removeTag(tag.id)}>&times;</button>
          </span>
        ))}
        <button
          className="tag-add-btn"
          onClick={() => setDropdownOpen(!dropdownOpen)}
        >
          +
        </button>
      </div>

      {dropdownOpen && (
        <div className="tag-dropdown">
          {grouped.map((group) => (
            <div key={group.value} className="tag-dropdown-group">
              <div className="tag-dropdown-label">{group.label}</div>
              {group.tags.map((tag) => (
                <button
                  key={tag.id}
                  className={`tag-dropdown-item ${currentIds.includes(tag.id) ? 'active' : ''}`}
                  onClick={() => toggleTag(tag.id)}
                >
                  <span className="tag-dot" style={{ background: tag.color }} />
                  {tag.name}
                  {currentIds.includes(tag.id) && <span className="tag-check">&#10003;</span>}
                </button>
              ))}
            </div>
          ))}

          {!creating ? (
            <button className="tag-create-trigger" onClick={() => setCreating(true)}>
              + Create new tag
            </button>
          ) : (
            <form className="tag-create-form" onSubmit={handleCreateTag}>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Tag name"
                autoFocus
              />
              <select value={newCategory} onChange={(e) => setNewCategory(e.target.value)}>
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              <div className="tag-color-picker">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`tag-color-swatch ${newColor === c ? 'active' : ''}`}
                    style={{ background: c }}
                    onClick={() => setNewColor(c)}
                  />
                ))}
              </div>
              <div className="tag-create-actions">
                <button type="submit" className="btn btn-sm">Create</button>
                <button type="button" className="btn btn-sm" onClick={() => setCreating(false)}>Cancel</button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
